/**
 * DesktopHeader — the imported design's 64px chrome (Task 6, spec §4.1).
 *
 * Mounted by `AppShell` (Task 7) ONLY when `useDesignWorld()` resolves light
 * — see that hook's docstring for why the imported design has no dark
 * variant and therefore never renders in dark theme (Ruling R17). Below the
 * design's own 1200px frame (spec §6/D2) `AppShell` keeps the incumbent
 * header too; this component makes no viewport decision of its own — it is
 * mounted whole or not at all, never partially collapsed.
 *
 * Composition, left to right (spec §4.1): wordmark -> nav pills (Assistant /
 * Shops / Library only — the design draws 3 destinations, not `NAV_ITEMS`'s
 * 4th `more` -> /settings tab; that destination is reached through the
 * account menu's own Settings row instead, so nothing is lost. Since
 * 2026-10-09 the owner hides Shops and Library from every nav for now, see
 * `HIDDEN_NAV_IDS` in AppShell, so the row draws Assistant alone) -> search ->
 * language pill (EN / RU / KG, the DESIGN's own order — not the app's
 * ru-first `SUPPORTED_LANGUAGES`) -> avatar.
 *
 * The search field and the avatar are REAL controls; the static design only
 * drew them as illustrative dead links (a span, a link to the auth screen).
 * Search submits to `/marketplace/search` (`HeaderSearch`, matching
 * `SearchResultsPage`'s own `q` param exactly). The avatar opens the
 * existing `AccountMenu` unmodified — its signed-in/out states, testids and
 * `useMe` wiring are reused, not rebuilt.
 *
 * Hit targets: the design's nav pills (~36px tall) and language segments
 * (~29px — the same "41×32" language pill auth-v2.css already documents)
 * both measure under the 44×44 floor, as does this wordmark link. Rather
 * than inflate them and move the design's own pixels, each carries
 * `.gd-hit-44` (design-world.css): an invisible ::after centred over the
 * visible box. NOT applied to the search `<input>` — see `HeaderSearch`'s
 * own docstring for why an overlay on a text field is the wrong tool there.
 */
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation } from "react-router-dom";

import { AccountMenu } from "@/app/AccountMenu";
import { VISIBLE_NAV_ITEMS, navTarget } from "@/app/AppShell";
import { HeaderSearch } from "@/app/shell/HeaderSearch";
import type { SupportedLanguage } from "@/lib/i18n";
import { useUiStore, type UiState } from "@/lib/ui-store";

/** The design's own display order (spec §4.1) — NOT `SUPPORTED_LANGUAGES`,
 *  which is ru-first (the app's default/fallback locale). */
const HEADER_LANGUAGES: readonly SupportedLanguage[] = ["en", "ru", "ky"];

const selectLanguage = (state: UiState): SupportedLanguage => state.language;
const selectSetLanguage = (state: UiState): UiState["setLanguage"] =>
  state.setLanguage;

const FOCUS_RING_CLASSES =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--gd-focus-ring)]";

export function DesktopHeader(): ReactElement {
  const { t } = useTranslation("common");
  const language = useUiStore(selectLanguage);
  const setLanguage = useUiStore(selectSetLanguage);
  const location = useLocation();
  // Computed INSIDE the component, not at module scope: `AppShell` imports
  // this component and this file imports the nav list back from `AppShell` —
  // a circular import. Filtering it at module top level ran during that
  // circular resolution, before `AppShell`'s own assignment had executed, and
  // threw ("Cannot read properties of undefined (reading 'filter')"). By
  // render time both modules have finished loading and the live binding is
  // correct. `VISIBLE_NAV_ITEMS` already leaves out the tabs the owner hides.
  const deskNavItems = VISIBLE_NAV_ITEMS.filter(
    (item) => item.enabled && item.id !== "more",
  );

  return (
    <header
      role="banner"
      data-testid="app-desktop-header"
      className="flex h-16 shrink-0 items-center gap-8 px-6"
      style={{
        backgroundColor: "var(--gd-ground)",
        borderBottom: "1px solid var(--gd-rule)",
        zIndex: 20,
      }}
    >
      {/* Wordmark. The «GardEn» logotype keeps the design's Bricolage through
          its own face token, while `--gd-display` is Onest like every other
          heading (owner, 2026-10-09; design-world.css). */}
      <Link
        to="/"
        data-testid="app-shell-wordmark"
        className={`gd-hit-44 inline-flex shrink-0 items-center rounded-[4px] ${FOCUS_RING_CLASSES}`}
        style={{
          fontFamily: "var(--gd-wordmark)",
          fontWeight: 800,
          fontSize: "24px",
          letterSpacing: "-0.01em",
          color: "var(--gd-primary)",
        }}
      >
        {t("appName")}
      </Link>

      {/* Nav pills — Assistant / Shops / Library (D3); Shops and Library are
          hidden for now (owner, 2026-10-09; `HIDDEN_NAV_IDS`). */}
      <nav aria-label={t("shell.primary")} className="flex items-center gap-[6px]">
        {deskNavItems.map((item) => (
          <NavLink
            key={item.id}
            to={navTarget(item, location.pathname).to}
            end={navTarget(item, location.pathname).end}
            data-testid={`nav-desktop-${item.id}`}
            className={({ isActive }) =>
              [
                "gd-hit-44 inline-flex items-center whitespace-nowrap rounded-full transition-colors",
                FOCUS_RING_CLASSES,
                isActive
                  ? ""
                  : "hover:bg-[color:var(--gd-wash-2)] hover:text-[color:var(--gd-primary)]",
              ]
                .filter(Boolean)
                .join(" ")
            }
            style={({ isActive }) => ({
              padding: "8px 18px",
              fontFamily: "var(--gd-text)",
              fontSize: "14px",
              fontWeight: 600,
              backgroundColor: isActive ? "var(--gd-primary)" : "transparent",
              color: isActive ? "var(--gd-on-primary)" : "var(--gd-ink-2)",
            })}
          >
            {t(item.labelKey)}
          </NavLink>
        ))}
      </nav>

      <div className="ml-auto flex shrink-0 items-center gap-6">
        <HeaderSearch />

        {/* Language pill — EN · RU · KG (the design's own order). */}
        <div className="flex shrink-0 items-center gap-1">
          {HEADER_LANGUAGES.map((lng) => {
            const active = lng === language;
            return (
              <button
                key={lng}
                type="button"
                aria-pressed={active}
                aria-label={t(`language.${lng}`)}
                data-testid={`language-switcher-${lng}`}
                onClick={() => setLanguage(lng)}
                className={[
                  "gd-hit-44 rounded-full transition-colors",
                  FOCUS_RING_CLASSES,
                  active
                    ? ""
                    : "hover:bg-[color:var(--gd-wash-2)] hover:text-[color:var(--gd-primary)]",
                ].join(" ")}
                style={{
                  padding: "6px 11px",
                  fontFamily: "var(--gd-text)",
                  fontSize: "12px",
                  fontWeight: 600,
                  backgroundColor: active ? "var(--gd-primary)" : "transparent",
                  color: active ? "var(--gd-on-primary)" : "var(--gd-ink-2)",
                }}
              >
                {t(`header.langCode.${lng}`)}
              </button>
            );
          })}
        </div>

        {/* Avatar — opens the existing account menu (not the design's link
            to the auth screen; that was a static prototype). */}
        <AccountMenu />
      </div>
    </header>
  );
}

export default DesktopHeader;
